import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { PageTitle, PageWrapper, Spinner } from "@cinesys/ui/components";
import styled from "styled-components";
import ApiService from "../../services/ApiService";
import SpotterUsageSectionView from "./SpotterUsageSection";

/**
 * Manager-side Usage tab (Monday 12650205737, round 3) — generalized from the Spotter-only
 * screen into a sections renderer. Fetches `GET /config/usage/:systemDomainId`, which already
 * filters sections server-side to plugins the org actually has installed (empty array otherwise
 * — see `apps/backend/src/api/manager/routes/usage.ts`). Dispatches each section to its renderer
 * by `section.plugin`; today Spotter is the only one registered.
 */

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px;
`;

const EmptyState = styled.div`
  color: var(--gray200);
  padding: 40px;
  text-align: center;
`;

function renderSection(section: UsageSection) {
  switch (section.plugin) {
    case "spotter":
      return <SpotterUsageSectionView section={section} />;
    default:
      return null;
  }
}

const UsageAdminScreen = () => {
  const { systemDomainId } = useParams<{ systemDomainId: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["usage", systemDomainId],
    enabled: !!systemDomainId,
    queryFn: async (): Promise<UsageSummaryResponse> => {
      const response = await ApiService.fetch<{ data: UsageSummaryResponse }>(
        "GET",
        `/api/config/usage/${systemDomainId}`
      );
      return response.data;
    },
  });

  return (
    <div>
      <PageTitle
        title="Usage"
        error={isError ? "Problem loading usage data" : undefined}
      />
      {isLoading ? (
        <Centered>
          <Spinner />
        </Centered>
      ) : (
        !isError &&
        data && (
          <PageWrapper>
            {data.sections.length === 0 ? (
              <EmptyState>No usage data for this org.</EmptyState>
            ) : (
              data.sections.map((section) => (
                <div key={section.plugin}>{renderSection(section)}</div>
              ))
            )}
          </PageWrapper>
        )
      )}
    </div>
  );
};

export default UsageAdminScreen;
